import React, { useEffect, useMemo, useState } from 'react';
import { Card, Table, Button, Badge, Row, Col, Form } from 'react-bootstrap';
import Pager from './Pager';
import { Bar, Doughnut } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Tooltip,
  Legend,
} from 'chart.js';
import { useNavigate } from 'react-router-dom';
import API from '../services/api';
import { formatDay } from '../utils/dates';

ChartJS.register(CategoryScale, LinearScale, BarElement, ArcElement, Tooltip, Legend);

const money = (n) => new Intl.NumberFormat('es-DO', { style: 'currency', currency: 'DOP' }).format(n || 0);
const dateStr = (d) => formatDay(d);
const MONTH_LABELS = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];

const STATUS_VARIANT = {
  draft: 'secondary',
  sent: 'primary',
  accepted: 'success',
  rejected: 'danger',
  expired: 'dark',
};

const STATUS_LABEL = {
  draft: 'Borrador',
  sent: 'Enviada',
  accepted: 'Aceptada',
  rejected: 'Rechazada',
  expired: 'Expirada',
};

const STATUS_COLOR = {
  draft: '#9DB3C2',
  sent: '#12B8B2',
  accepted: '#4ADE80',
  rejected: '#F87171',
  expired: '#6B7280',
};

const PENDING_STATUSES = ['draft', 'sent'];

const QuotesPage = () => {
  const [quotes, setQuotes] = useState([]);
  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [convertingId, setConvertingId] = useState(null);
  const [convertError, setConvertError] = useState('');
  const [waNumber, setWaNumber] = useState('');
  const [waNote, setWaNote] = useState(null); // { variant, text }
  const [waBusy, setWaBusy] = useState(false);
  const pageSize = 10;
  const navigate = useNavigate();

  const loadQuotes = () => {
    API.get('/quotes').then(res => setQuotes(res.data)).catch(err => console.error('Error loading quotes:', err));
  };

  useEffect(loadQuotes, []);

  // A quote made by WhatsApp can have lines that aren't products and a client who isn't one yet: it becomes a factura in
  // the Facturas builder, which opens with its lines so the client and the rest can be reviewed first.
  const viaBuilder = (q) => q.source === 'whatsapp' || !q.client;

  // Its PDF is the one sent by WhatsApp. The tab is opened right away (a pop-up opened after the download would be blocked).
  const openPdf = async (q) => {
    setConvertError('');
    const tab = window.open('', '_blank');
    try {
      const { data } = await API.get(`/quotes/${q._id}/pdf`, { responseType: 'blob' });
      if (tab) tab.location.href = URL.createObjectURL(data);
    } catch (err) {
      if (tab) tab.close();
      setConvertError('No se pudo abrir el PDF de la cotización.');
    }
  };

  // "Cotización de WhatsApp": the number Cilindrito gave → the Facturas builder with all its lines.
  const handleOpenByNumber = async (e) => {
    e.preventDefault();
    if (!waNumber.trim()) return;
    setWaNote(null);
    setWaBusy(true);
    try {
      const { data } = await API.get(`/quotes/by-number/${encodeURIComponent(waNumber.trim())}`);
      if (data.invoiceNumber) setWaNote({ variant: 'warning', text: `La cotización ${data.number} ya se convirtió en la factura ${data.invoiceNumber}.` });
      else navigate(`/invoices/new?quote=${data._id}`);
    } catch (err) {
      setWaNote({ variant: 'danger', text: err.response?.data?.message || 'No se pudo buscar la cotización.' });
    } finally {
      setWaBusy(false);
    }
  };

  const handleConvert = async (id) => {
    setConvertError('');
    setConvertingId(id);
    try {
      const { data } = await API.post('/invoices', { quoteId: id });
      window.open(`/invoices/${data._id}/print`, '_blank');
      loadQuotes();
    } catch (err) {
      setConvertError(err.response?.data?.message || 'Error al convertir la cotización a factura.');
    } finally {
      setConvertingId(null);
    }
  };

  const stats = useMemo(() => {
    const now = new Date();
    const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());

    const cotizadoHoy = quotes
      .filter(q => new Date(q.createdAt) >= todayStart)
      .reduce((sum, q) => sum + q.total, 0);

    const pendingCount = quotes.filter(q => PENDING_STATUSES.includes(q.status)).length;

    const monthly = {};
    quotes.forEach(q => {
      const d = new Date(q.createdAt);
      const key = `${d.getFullYear()}-${d.getMonth()}`;
      monthly[key] = (monthly[key] || 0) + q.total;
    });
    const monthlyLabels = [];
    const monthlyValues = [];
    for (let i = 5; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const key = `${d.getFullYear()}-${d.getMonth()}`;
      monthlyLabels.push(`${MONTH_LABELS[d.getMonth()]} ${d.getFullYear()}`);
      monthlyValues.push(monthly[key] || 0);
    }

    const byStatus = { draft: 0, sent: 0, accepted: 0, rejected: 0, expired: 0 };
    quotes.forEach(q => { byStatus[q.status] = (byStatus[q.status] || 0) + 1; });

    return { cotizadoHoy, pendingCount, monthlyLabels, monthlyValues, byStatus };
  }, [quotes]);

  const filteredQuotes = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return quotes;
    return quotes.filter(q =>
      q.number?.toLowerCase().includes(term) ||
      (q.client?.name || q.clientName)?.toLowerCase().includes(term)
    );
  }, [quotes, search]);

  useEffect(() => {
    setCurrentPage(1);
  }, [search]);

  const totalPages = Math.max(1, Math.ceil(filteredQuotes.length / pageSize));
  const pagedQuotes = filteredQuotes.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  const monthlyChart = {
    labels: stats.monthlyLabels,
    datasets: [{ label: 'Cotizado', data: stats.monthlyValues, backgroundColor: 'rgba(18,184,178,0.6)', borderRadius: 4 }],
  };

  const statusKeys = Object.keys(STATUS_LABEL);
  const statusChart = {
    labels: statusKeys.map(k => STATUS_LABEL[k]),
    datasets: [{
      data: statusKeys.map(k => stats.byStatus[k] || 0),
      backgroundColor: statusKeys.map(k => STATUS_COLOR[k]),
      hoverOffset: 4,
    }],
  };

  return (
    <div className="container-fluid p-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2 className="app-title" style={{ textAlign: 'left', margin: 0 }}>Cotizaciones</h2>
        <Button onClick={() => navigate('/quotes/new')}>+ Nueva Cotización</Button>
      </div>

      <Row className="mb-3">
        <Col md={6}>
          <Card className="shadow-sm p-3 dashboard-card">
            <div className="text-muted small">Cotizado Hoy</div>
            <h2 className="mt-2 mb-0" style={{ color: 'var(--mm-cyan)' }}>{money(stats.cotizadoHoy)}</h2>
          </Card>
        </Col>
        <Col md={6}>
          <Card className="shadow-sm p-3 dashboard-card">
            <div className="text-muted small">Cotizaciones Pendientes</div>
            <h2 className="mt-2 mb-0">{stats.pendingCount}</h2>
          </Card>
        </Col>
      </Row>

      <Card className="shadow-sm p-3 mb-3">
        <Form onSubmit={handleOpenByNumber} className="d-flex flex-wrap align-items-end gap-2">
          <div>
            <Form.Label className="mb-1"><i className="bi bi-whatsapp" /> Facturar una cotización de WhatsApp</Form.Label>
            <Form.Control
              style={{ width: 240 }}
              placeholder="Número (ej.: CT123456)"
              value={waNumber}
              onChange={e => setWaNumber(e.target.value)}
            />
          </div>
          <Button type="submit" disabled={waBusy || !waNumber.trim()}>{waBusy ? 'Buscando...' : 'Abrir'}</Button>
          <div className="text-muted small" style={{ flex: '1 1 260px' }}>
            Escribe el número que dio Cilindrito: se abre una factura con todas sus líneas (también las que no son productos del sistema) para elegir el cliente, revisarla y crearla.
          </div>
        </Form>
        {waNote && <div className={`alert alert-${waNote.variant} mt-3 mb-0`}>{waNote.text}</div>}
      </Card>

      <Card className="shadow-sm p-3">
        {convertError && <div className="alert alert-danger">{convertError}</div>}
        <Form.Control
          className="mb-3"
          style={{ maxWidth: 320 }}
          placeholder="Buscar por número o cliente..."
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <Table hover responsive className="mb-0">
          <thead>
            <tr>
              <th>No.</th><th>Cliente</th><th>Fecha</th><th>Válida hasta</th>
              <th className="text-end">Total</th><th>Estado</th><th></th>
            </tr>
          </thead>
          <tbody>
            {pagedQuotes.map(q => (
              <tr key={q._id}>
                <td>{q.number}{q.source === 'whatsapp' && <i className="bi bi-whatsapp ms-2" title="Hecha por WhatsApp" style={{ color: 'var(--mm-cyan)' }} />}</td>
                <td>{q.client?.name || (q.clientName ? <>{q.clientName} <span className="text-muted small">(sin cliente en Ventas)</span></> : '—')}</td>
                <td>{dateStr(q.createdAt)}</td>
                <td>{dateStr(q.validUntil)}</td>
                <td className="text-end">{money(q.total)}</td>
                <td><Badge bg={STATUS_VARIANT[q.status]}>{STATUS_LABEL[q.status] || q.status}</Badge></td>
                <td className="text-end">
                  <div className="d-flex gap-2 justify-content-end">
                    {PENDING_STATUSES.includes(q.status) && (
                      <Button
                        size="sm"
                        variant="outline-primary"
                        disabled={convertingId === q._id}
                        onClick={() => (viaBuilder(q) ? navigate(`/invoices/new?quote=${q._id}`) : handleConvert(q._id))}
                      >
                        {convertingId === q._id ? 'Convirtiendo...' : 'Convertir a Factura'}
                      </Button>
                    )}
                    {q.status !== 'accepted' && (
                      <Button size="sm" variant="outline-secondary" onClick={() => navigate(`/quotes/${q._id}/edit`)}>
                        <i className="bi bi-pencil" /> Editar
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="outline-secondary"
                      onClick={() => (q.source === 'whatsapp' ? openPdf(q) : window.open(`/quotes/${q._id}/print`, '_blank'))}
                    >
                      <i className="bi bi-printer" /> Ver / Imprimir
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
            {filteredQuotes.length === 0 && (
              <tr><td colSpan={7} className="text-muted text-center">
                {search ? `Ninguna cotización coincide con "${search}".` : 'Sin cotizaciones todavía.'}
              </td></tr>
            )}
          </tbody>
        </Table>

        {totalPages > 1 && (
          <div className="d-flex justify-content-between align-items-center mt-3">
            <span className="text-muted small">
              Página {currentPage} de {totalPages} — {filteredQuotes.length} cotización{filteredQuotes.length === 1 ? '' : 'es'}
            </span>
            <Pager page={currentPage} pages={totalPages} onChange={setCurrentPage} />
          </div>
        )}
      </Card>

      <Row className="mt-3">
        <Col md={6}>
          <Card className="shadow-sm p-3 card-equal-height">
            <Card.Title>Cotizado por Mes</Card.Title>
            <div style={{ height: 280 }}>
              <Bar
                data={monthlyChart}
                options={{ responsive: true, maintainAspectRatio: false, plugins: { legend: { display: false } } }}
              />
            </div>
          </Card>
        </Col>
        <Col md={6}>
          <Card className="shadow-sm p-3 card-equal-height">
            <Card.Title>Cotizaciones por Estado</Card.Title>
            <div style={{ height: 280 }}>
              <Doughnut
                data={statusChart}
                options={{ responsive: true, maintainAspectRatio: false, plugins: { legend: { position: 'bottom' } } }}
              />
            </div>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default QuotesPage;
